import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';

  const filteredComics = useMemo(() => {
    if (activeSlug === 'all') return comics;
    if (activeSlug === 'completed') return comics.filter((c) => c.status === '已完结');
    return comics.filter((c) => c.category === activeSlug);
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const categoryTitle = activeCategory ? activeCategory.name : '全部';
  const seoTitle = `${categoryTitle}韩国漫画 - 韩漫在线免费阅读 - 韩国漫画网`;
  const seoDesc = `浏览${categoryTitle}韩国漫画，韩漫在线免费阅读。韩国漫画网为您精选${categoryTitle}题材的热门韩漫作品，每日更新，海量资源一网打尽。`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`${categoryTitle}韩国漫画,韩漫在线免费阅读,韩国漫画网,热门韩漫,${categoryTitle}漫画`}
        canonicalUrl={`https://www.koreanmanhwa.com/category/${activeSlug}`}
        ogTitle={seoTitle}
        ogDescription={seoDesc}
        ogType="website"
        ogUrl={`https://www.koreanmanhwa.com/category/${activeSlug}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${categoryTitle}韩国漫画 - 韩国漫画网`,
            "description": seoDesc,
            "url": `https://www.koreanmanhwa.com/category/${activeSlug}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "韩国漫画网",
              "url": "https://www.koreanmanhwa.com/"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.koreanmanhwa.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": `${categoryTitle}韩国漫画`,
                "item": `https://www.koreanmanhwa.com/category/${activeSlug}`
              }
            ]
          }
        ]}
      />

      <Navbar />
      <main className="min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-20 pb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${categoryTitle}韩国漫画` },
            ]}
          />
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              <strong>{categoryTitle}韩国漫画</strong>
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {activeSlug === 'all'
                ? '浏览全部韩国漫画作品，最新最热韩漫一网打尽'
                : activeSlug === 'completed'
                  ? '已完结的韩国漫画作品合集，一口气看到底'
                  : `精选${categoryTitle}题材韩国漫画，满足您的阅读口味`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-bookmark-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无作品，请浏览其他分类</p>
              <Link to="/category/all" className="inline-block mt-3 text-sm text-primary-500 hover:text-primary-600 font-medium">
                查看全部漫画 →
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}